import type { ProjectPage } from '@canvas/shared-types';
import { runEditorCommand } from '@/lib/editorCommands';
import { MOTION_EXPORT_DESKTOP_COPY } from '@/lib/video/browserSupport';
import { useVideoStore } from '@/stores/videoStore';
import EditorZoomControls from './EditorZoomControls';

interface MobileBottomBarProps {
  zoom: number;
  onZoomChange: (zoom: number) => void;
  onZoomFit: () => void;
  artboardLabel: string;
  pages: ProjectPage[];
  activePageId: string;
  thumbnails?: Record<string, string>;
  onSelectPage: (pageId: string) => void;
  onAddPage: () => void;
  onDuplicatePage: () => void;
  onDeletePage: () => void;
  /** Motion mode: preview from here; export stays on desktop. */
  motionMode?: boolean;
}

export default function MobileBottomBar({
  zoom,
  onZoomChange,
  onZoomFit,
  artboardLabel,
  pages,
  activePageId,
  thumbnails = {},
  onSelectPage,
  onAddPage,
  onDuplicatePage,
  onDeletePage,
  motionMode = false,
}: MobileBottomBarProps) {
  const playing = useVideoStore((s) => motionMode && s.playback === 'playing');
  return (
    <div className="ps-mobile-bottom-bar" aria-label="Zoom, size, and pages">
      {motionMode && (
        <div className="ps-mobile-bottom-row">
          <button
            type="button"
            className="ps-mobile-bottom-btn accent"
            onClick={() => useVideoStore.getState().togglePlay()}
          >
            {playing ? 'Pause' : 'Play'}
          </button>
          <button
            type="button"
            className="ps-mobile-bottom-btn"
            title={MOTION_EXPORT_DESKTOP_COPY}
            onClick={() => useVideoStore.getState().setNotice(MOTION_EXPORT_DESKTOP_COPY)}
          >
            Export
          </button>
          <span className="ps-mobile-bottom-label">Preview here · export on desktop</span>
        </div>
      )}
      <div className="ps-mobile-bottom-row">
        <EditorZoomControls
          variant="mobile"
          zoom={zoom}
          onZoomChange={onZoomChange}
          onZoomFit={onZoomFit}
        />

        <button
          type="button"
          className="ps-mobile-size-btn"
          title="Change artboard size"
          onClick={() => runEditorCommand('artboard-size')}
        >
          <span className="ps-mobile-bottom-label">Size</span>
          <span className="ps-mobile-size-value">{artboardLabel}</span>
        </button>
      </div>

      <div className="ps-mobile-pages" aria-label="Pages">
        <span className="ps-mobile-bottom-label">{motionMode ? 'Scenes' : 'Pages'}</span>
        <div className="ps-mobile-pages-scroll">
          {pages.map((page, i) => {
            const active = page.pageId === activePageId;
            const thumb = thumbnails[page.pageId];
            return (
              <button
                key={page.pageId}
                type="button"
                className={`ps-mobile-page-chip ${active ? 'active' : ''} ${thumb ? 'has-thumb' : ''}`}
                title={page.name}
                aria-current={active ? 'page' : undefined}
                onClick={() => onSelectPage(page.pageId)}
              >
                {thumb ? (
                  <img className="ps-mobile-page-thumb" src={thumb} alt="" draggable={false} />
                ) : (
                  <span>{i + 1}</span>
                )}
              </button>
            );
          })}
        </div>
        <div className="ps-mobile-pages-actions">
          <button type="button" className="ps-mobile-bottom-btn accent" onClick={onAddPage}>
            + Page
          </button>
          <button type="button" className="ps-mobile-bottom-btn" title="Duplicate page" onClick={onDuplicatePage}>
            Dup
          </button>
          <button
            type="button"
            className="ps-mobile-bottom-btn"
            title="Delete page"
            disabled={pages.length <= 1}
            onClick={onDeletePage}
          >
            Del
          </button>
        </div>
      </div>
    </div>
  );
}
